import React from 'react';
import Button from '../../components/Button';
import AdminSidebar from '../../components/admin/AdminSidebar';

export default function AdminManagementView({
  admins,
  loading,
  error,
  newAdminEmail,
  onInputChange,
  onAddAdmin,
  onRemoveAdmin,
  success,
  successMessage
}) {
  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 text-gray-100">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 text-blue-400 text-left">Admin Management</h1>
        <p className="text-gray-400 text-left">Add and remove admin permissions for system users</p>
      </div>
      
      <div className="flex flex-col lg:flex-row gap-8">
        {/* Admin Sidebar */}
        <AdminSidebar />
        
        {/* Main Content */}
        <main className="flex-1">
          <div className="bg-gray-900 rounded-xl shadow-xl p-6 mb-8 border border-gray-800">
            {/* Add new admin form */}
            <div className="mb-10">
              <h2 className="text-xl font-semibold mb-4 text-blue-400 text-left">Add New Admin</h2>
              
              {error && (
                <div className="bg-red-900/30 border-l-4 border-red-500 text-red-300 p-4 mb-4 rounded-r text-left" role="alert">
                  <p>{error}</p>
                </div>
              )}
              
              {success && (
                <div className="bg-green-900/30 border-l-4 border-green-500 text-green-300 p-4 mb-4 rounded-r text-left" role="alert">
                  <p>{successMessage}</p>
                </div>
              )}
              
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-grow">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={newAdminEmail}
                    onChange={onInputChange}
                    placeholder="Enter user email"
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-left"
                    dir="ltr"
                  />
                </div>
                <div>
                  <button
                    onClick={onAddAdmin}
                    disabled={loading}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center justify-center whitespace-nowrap"
                  >
                    {loading ? (
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : 'Add Admin'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2 text-left">
                * User must be registered in the system to add admin permissions
              </p>
            </div>
            
            {/* Admins list */}
            <div>
              <h2 className="text-xl font-semibold mb-4 text-blue-400 text-left">Existing Admins</h2>
              
              {loading && !admins.length ? (
                <div className="flex justify-center my-8">
                  <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : admins.length === 0 ? (
                <div className="bg-gray-800/50 p-6 rounded-lg text-center">
                  <p className="text-gray-400">No admins found in the system.</p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-lg ring-1 ring-gray-700">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead>
                      <tr>
                        <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Name</th>
                        <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Email</th>
                        <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Date Added</th>
                        <th className="py-3 px-4 bg-gray-800 text-left font-medium text-gray-300">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                      {admins.map((admin, index) => (
                        <tr key={admin.uid} className="bg-gray-900 hover:bg-gray-800 transition-colors duration-150">
                          <td className="py-3 px-4">{admin.displayName || 'Not specified'}</td>
                          <td className="py-3 px-4">{admin.email}</td>
                          <td className="py-3 px-4">
                            {admin.addedAt 
                              ? (typeof admin.addedAt === 'object' && admin.addedAt.seconds 
                                  ? new Date(admin.addedAt.seconds * 1000).toLocaleString() 
                                  : typeof admin.addedAt === 'number'
                                    ? new Date(admin.addedAt).toLocaleString()
                                    : typeof admin.addedAt === 'string'
                                      ? new Date(admin.addedAt).toLocaleString()
                                      : 'Not specified')
                              : 'Not specified'}
                          </td>
                          <td className="py-3 px-4">
                            <button
                              onClick={() => onRemoveAdmin(admin.uid)}
                              className={`px-4 py-1.5 rounded-lg text-sm ${admin.isOldestAdmin 
                                ? 'bg-gray-700 text-gray-400 cursor-not-allowed' 
                                : 'bg-red-900/40 text-red-300 hover:bg-red-900/60 transition-colors duration-200'}`}
                              disabled={loading || admin.isOldestAdmin}
                            >
                              Remove Admin
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
} 